import type { FuelPriceChangeSnapshot, FuelStalePriceAlert } from "@station/contracts";
import { addDays, type LocalDate, toIstanbulDate } from "@station/core";
import {
  Alert,
  Card,
  DataTable,
  type DataTableColumn,
  DateRangePicker,
  Drawer,
  EmptyState,
  FormField,
  MoneyText,
  QuantityText,
  Select,
  SkeletonRows,
  StatusBadge,
} from "@station/ui";
import { createFileRoute } from "@tanstack/react-router";
import { BadgeCheck, Tags } from "lucide-react";
import { type ReactElement, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useCompanyStations } from "../../../features/accounting/shared.js";
import { useStationScope } from "../../../features/cashflow/shared.js";
import { Field, Section } from "../../../features/expenses/shared.js";
import {
  PRICE_DIFFERENCE_TONE,
  priceDifferenceDirection,
  priceDifferenceSign,
} from "../../../features/fuel/price-change-values.js";
import { useFuelPermissions, usePriceChangeReport } from "../../../features/fuel/queries.js";
import { FuelPage, fuelSearchShape, Th } from "../../../features/fuel/shared.js";
import { errorMessage } from "../../../lib/errors.js";
import { formatCount, formatDateTime, formatPriceChange, formatUnitPrice } from "../../../lib/format.js";

const localDateText = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()
  .catch(undefined);

const searchSchema = z.object({
  ...fuelSearchShape,
  from: localDateText,
  to: localDateText,
  snapshot: z.string().uuid().optional().catch(undefined),
});
type PriceReportSearch = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/_app/akaryakit/fiyat-farki")({
  validateSearch: (search) => searchSchema.parse(search),
  component: PriceChangeReportPage,
});

/**
 * Fiyat farkı raporu. Fark = yürürlük anındaki stok × (yeni − eski fiyat); **bilgi amaçlıdır, muhasebe
 * kaydı DEĞİLDİR** (ADR 0012 §13) — bu uyarı ekranda görünür durur, dipnotta değil.
 *
 * Rapor ŞİRKET genelidir; istasyon süzgeci isteğe bağlıdır.
 */
function PriceChangeReportPage(): ReactElement {
  const { t } = useTranslation();
  const permissions = useFuelPermissions();
  return (
    <FuelPage
      title={t("menu.fuelPriceDifference")}
      description={t("fuel.priceReport.description")}
      breadcrumb={[{ label: t("menu.fuelPriceDifference") }]}
    >
      {({ companyId }) =>
        permissions.canRead ? (
          <PriceChangeReportBody companyId={companyId} />
        ) : (
          <Alert tone="info">{t("fuel.noPermission")}</Alert>
        )
      }
    </FuelPage>
  );
}

function defaultRange(): { from: LocalDate; to: LocalDate } {
  const today = toIstanbulDate(new Date());
  return { from: addDays(today, -6), to: today };
}

function PriceChangeReportBody({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const stations = useCompanyStations(companyId);
  const scope = useStationScope();
  const fallback = defaultRange();
  const range = { from: search.from ?? fallback.from, to: search.to ?? fallback.to };

  const setSearch = (patch: Partial<PriceReportSearch>) =>
    void navigate({ search: (previous) => ({ ...previous, ...patch }), replace: true });

  const visible = useMemo(
    () =>
      (stations.data ?? []).filter(
        (station) => !scope.restricted || scope.allowedStationIds.includes(station.id),
      ),
    [stations.data, scope.restricted, scope.allowedStationIds],
  );
  // Kapsamı kısıtlı kullanıcıda "şirket geneli" anlamsızdır: ilk görünür istasyon varsayılan süzgeç olur.
  const fallbackStationId = scope.restricted ? (visible[0]?.id ?? null) : null;
  const stationId =
    search.station && visible.some((station) => station.id === search.station)
      ? search.station
      : fallbackStationId;

  const report = usePriceChangeReport(companyId, stationId, range);
  const snapshots = (report.data?.snapshots ?? []) as FuelPriceChangeSnapshot[];
  const alerts = (report.data?.staleSaleAlerts ?? []) as FuelStalePriceAlert[];
  const selected = snapshots.find((snapshot) => snapshot.id === search.snapshot) ?? null;

  const columns = useMemo<DataTableColumn<FuelPriceChangeSnapshot>[]>(
    () => [
      {
        id: "effectiveAt",
        header: t("fuel.priceReport.fields.effectiveAt"),
        hideable: false,
        exportValue: (row) => formatDateTime(row.effectiveAt),
        cell: (row) => formatDateTime(row.effectiveAt),
      },
      {
        id: "station",
        header: t("fuel.station"),
        exportValue: (row) => row.stationName,
        cell: (row) => row.stationName,
      },
      {
        id: "product",
        header: t("fuel.sales.fields.product"),
        exportValue: (row) => row.productName,
        cell: (row) => row.productName,
      },
      {
        id: "price",
        header: t("fuel.priceReport.fields.price"),
        align: "right",
        exportValue: (row) => row.newUnitPrice,
        cell: (row) => (
          <span className="grid justify-items-end">
            <span>
              {formatUnitPrice(row.previousUnitPrice)} → {formatUnitPrice(row.newUnitPrice)}
            </span>
            <span className="text-xs text-muted-foreground">
              {formatPriceChange(row.previousUnitPrice, row.newUnitPrice)}
            </span>
          </span>
        ),
      },
      {
        id: "stockL",
        header: t("fuel.priceReport.fields.stock"),
        align: "right",
        exportValue: (row) => row.stockL,
        cell: (row) => <QuantityText value={row.stockL} unit="LITRE" />,
      },
      {
        id: "differenceAmount",
        header: t("fuel.priceReport.fields.difference"),
        align: "right",
        exportValue: (row) => row.differenceAmount,
        cell: (row) => <DifferenceAmount amount={row.differenceAmount} />,
      },
      {
        id: "requestNo",
        header: t("fuel.priceReport.fields.requestNo"),
        defaultVisible: false,
        exportValue: (row) => row.requestNo,
        cell: (row) => row.requestNo ?? "—",
      },
    ],
    [t],
  );

  if (stations.error) return <Alert tone="danger">{errorMessage(stations.error)}</Alert>;

  return (
    <>
      <Card className="flex flex-wrap items-end gap-3 p-4">
        <DateRangePicker
          label={t("fuel.priceReport.range")}
          value={{ from: range.from as LocalDate, to: range.to as LocalDate }}
          onValueChange={(next) =>
            setSearch({ from: next.from ?? undefined, to: next.to ?? undefined, snapshot: undefined })
          }
        />
        <FormField label={t("fuel.station")} className="w-64">
          {(control) => (
            <Select
              {...control}
              value={stationId ?? ""}
              onChange={(event) =>
                setSearch({ station: event.target.value || undefined, snapshot: undefined })
              }
            >
              {scope.restricted ? null : <option value="">{t("fuel.priceReport.allStations")}</option>}
              {visible.map((station) => (
                <option key={station.id} value={station.id}>
                  {station.code} {station.name}
                </option>
              ))}
            </Select>
          )}
        </FormField>
      </Card>

      <Section title={t("fuel.priceReport.summaryTitle")}>
        {/* Uyarı DİPNOT değildir: fark tutarı muhasebe kaydı sanılmasın diye özetin içinde durur. */}
        <Alert tone="info">{t("fuel.priceReport.notAccounting")}</Alert>
        <dl className="grid gap-3 text-sm sm:grid-cols-3">
          <Field label={t("fuel.priceReport.fields.totalDifference")}>
            {report.data ? <MoneyText value={report.data.totalDifferenceAmount} colorize /> : "—"}
          </Field>
          <Field label={t("fuel.priceReport.fields.snapshotCount")}>{formatCount(snapshots.length)}</Field>
          <Field label={t("fuel.priceReport.fields.alertCount")}>{formatCount(alerts.length)}</Field>
        </dl>
      </Section>

      <DataTable<FuelPriceChangeSnapshot>
        caption={t("fuel.priceReport.caption")}
        columns={columns}
        rows={snapshots}
        rowKey={(row) => row.id}
        loading={report.isPending}
        error={report.error ? <Alert tone="danger">{errorMessage(report.error)}</Alert> : undefined}
        emptyState={
          <EmptyState
            headingLevel={2}
            icon={<Tags />}
            title={t("fuel.priceReport.emptyTitle")}
            description={t("fuel.priceReport.emptyDescription")}
          />
        }
        onRowClick={(row) => setSearch({ snapshot: row.id })}
      />
      <p className="text-xs text-muted-foreground">{t("fuel.priceReport.rowHint")}</p>

      <StaleAlertsSection alerts={alerts} loading={report.isPending} />

      <SnapshotDrawer item={selected} onClose={() => setSearch({ snapshot: undefined })} />
    </>
  );
}

function DifferenceAmount({ amount }: { amount: string | null }): ReactElement {
  const { t } = useTranslation();
  const direction = priceDifferenceDirection(amount);
  if (amount === null) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="flex flex-wrap items-center justify-end gap-2">
      <span aria-hidden="true">{priceDifferenceSign(direction)}</span>
      <MoneyText value={amount} />
      <StatusBadge tone={PRICE_DIFFERENCE_TONE[direction]}>
        {t(`fuel.priceReport.direction.${direction}`)}
      </StatusBadge>
    </span>
  );
}

/** Eski fiyattan satış: pompa otomasyonu yeni fiyatı almamış demektir — boşsa OLUMLU boş durum gösterilir. */
function StaleAlertsSection({
  alerts,
  loading,
}: {
  alerts: readonly FuelStalePriceAlert[];
  loading: boolean;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <Section title={t("fuel.priceReport.alertsTitle")}>
      <p className="text-sm text-muted-foreground">{t("fuel.priceReport.alertsHint")}</p>
      {loading ? (
        <SkeletonRows rows={3} />
      ) : alerts.length === 0 ? (
        <EmptyState
          headingLevel={3}
          icon={<BadgeCheck />}
          title={t("fuel.priceReport.alertsEmptyTitle")}
          description={t("fuel.priceReport.alertsEmptyDescription")}
        />
      ) : (
        <div
          className="overflow-x-auto rounded-lg border"
          tabIndex={0}
          role="group"
          aria-label={t("fuel.priceReport.alertsTable")}
        >
          <table className="w-full text-sm">
            <caption className="sr-only">{t("fuel.priceReport.alertsTable")}</caption>
            <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
              <tr>
                <Th>{t("fuel.totalizers.nozzle")}</Th>
                <Th>{t("fuel.sales.fields.product")}</Th>
                <Th align="right">{t("fuel.priceReport.fields.expectedPrice")}</Th>
                <Th align="right">{t("fuel.priceReport.fields.actualPrice")}</Th>
                <Th>{t("fuel.priceReport.fields.effectiveAt")}</Th>
                <Th>{t("fuel.priceReport.fields.firstTransactionAt")}</Th>
              </tr>
            </thead>
            <tbody>
              {alerts.map((alert) => (
                <tr key={alert.id} className="border-t bg-warning-subtle">
                  <td className="px-3 py-2">{alert.nozzleLabel}</td>
                  <td className="px-3 py-2">{alert.productName}</td>
                  <td className="px-3 py-2 text-right">{formatUnitPrice(alert.expectedUnitPrice)}</td>
                  <td className="px-3 py-2 text-right">{formatUnitPrice(alert.actualUnitPrice)}</td>
                  <td className="px-3 py-2">{formatDateTime(alert.effectiveAt)}</td>
                  <td className="px-3 py-2">{formatDateTime(alert.firstTransactionAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

/** Satır ayrıntısı: farkın dayandığı STOK nasıl bulundu — tank tank döküm. */
function SnapshotDrawer({
  item,
  onClose,
}: {
  item: FuelPriceChangeSnapshot | null;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <Drawer
      open={item !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={
        item
          ? t("fuel.priceReport.detailTitle", {
              product: item.productName,
              at: formatDateTime(item.effectiveAt),
            })
          : ""
      }
    >
      {item === null ? null : (
        <div className="grid gap-4">
          <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
            <Field label={t("fuel.station")}>{item.stationName}</Field>
            <Field label={t("fuel.priceReport.fields.requestNo")}>{item.requestNo ?? "—"}</Field>
            <Field label={t("fuel.priceReport.fields.previousPrice")}>
              {formatUnitPrice(item.previousUnitPrice)}
            </Field>
            <Field label={t("fuel.priceReport.fields.newPrice")}>{formatUnitPrice(item.newUnitPrice)}</Field>
            <Field label={t("fuel.priceReport.fields.stock")}>
              <QuantityText value={item.stockL} unit="LITRE" />
            </Field>
            <Field label={t("fuel.priceReport.fields.difference")}>
              <DifferenceAmount amount={item.differenceAmount} />
            </Field>
          </dl>

          <h3 className="text-sm font-semibold">{t("fuel.priceReport.tankBreakdown")}</h3>
          <p className="text-xs text-muted-foreground">{t("fuel.priceReport.tankBreakdownHint")}</p>
          <div
            className="overflow-x-auto rounded-lg border"
            tabIndex={0}
            role="group"
            aria-label={t("fuel.priceReport.tankBreakdown")}
          >
            <table className="w-full text-sm">
              <caption className="sr-only">{t("fuel.priceReport.tankBreakdown")}</caption>
              <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
                <tr>
                  <Th>{t("fuel.priceReport.fields.tank")}</Th>
                  <Th>{t("fuel.priceReport.fields.lastReadingAt")}</Th>
                  <Th align="right">{t("fuel.priceReport.fields.lastReadingVolume")}</Th>
                  <Th align="right">{t("fuel.priceReport.fields.salesSince")}</Th>
                  <Th align="right">{t("fuel.priceReport.fields.deliveredSince")}</Th>
                  <Th align="right">{t("fuel.priceReport.fields.stock")}</Th>
                </tr>
              </thead>
              <tbody>
                {item.tankDetails.map((tank) => (
                  <tr key={tank.tankId} className="border-t">
                    <td className="px-3 py-2">{t("fuel.priceReport.tankNo", { tankNo: tank.tankNo })}</td>
                    <td className="px-3 py-2">{formatDateTime(tank.lastReadingAt)}</td>
                    <td className="px-3 py-2 text-right">
                      <QuantityText value={tank.lastReadingVolumeL} unit="LITRE" />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <QuantityText value={tank.salesSinceReadingL} unit="LITRE" />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <QuantityText value={tank.deliveredSinceReadingL} unit="LITRE" />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <QuantityText value={tank.stockL} unit="LITRE" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Drawer>
  );
}
